import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['input', 'status']

  connect () {
    this.persistedSnapshot = this.snapshot()
    this.pendingSnapshot = null
    this.requestInFlight = false
    if (this.hasStatusTarget) this.statusTarget.textContent = ''
  }

  preventSubmit (event) {
    event.preventDefault()
  }

  queue () {
    this.pendingSnapshot = this.snapshot()
    this.drainQueue()
  }

  async changeLanguage (event) {
    const locale = event.currentTarget.dataset.locale
    if (!locale || this.languageRequestInFlight) return
    this.languageRequestInFlight = true

    await this.waitForQueue()
    try {
      const response = await fetch(this.element.action, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector("meta[name='csrf-token']")?.content || ''
        },
        body: JSON.stringify({ rider: { interface_language: locale } })
      })
      if (!response.ok) throw new Error('Unable to save language')
      this.setStatus('saved')
      window.location.assign(`${window.location.pathname}?locale=${encodeURIComponent(locale)}`)
    } catch (error) {
      this.setStatus('error')
    } finally {
      this.languageRequestInFlight = false
    }
  }

  waitForQueue () {
    if (!this.requestInFlight && !this.pendingSnapshot) return Promise.resolve()
    return new Promise((resolve) => {
      this.queueSettled = resolve
    })
  }

  snapshot () {
    return {
      preferred_travel_modes: this.inputTargets.filter((input) => input.name === 'rider[preferred_travel_modes][]' && input.checked).map((input) => input.value),
      email_notifications: this.inputTargets.find((input) => input.name === 'rider[email_notifications]')?.checked || false,
      sms_notifications: this.inputTargets.find((input) => input.name === 'rider[sms_notifications]')?.checked || false
    }
  }

  async drainQueue () {
    if (this.requestInFlight || !this.pendingSnapshot) return

    const snapshot = this.pendingSnapshot
    this.pendingSnapshot = null
    this.requestInFlight = true

    try {
      const response = await fetch(this.element.action, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector("meta[name='csrf-token']")?.content || ''
        },
        body: JSON.stringify({ rider: snapshot })
      })
      const payload = await response.json()
      if (!response.ok) throw new Error(payload.errors?.join(', ') || 'Unable to save preferences')

      this.persistedSnapshot = payload
      this.setStatus('saved')
    } catch (error) {
      this.restore(this.persistedSnapshot)
      this.pendingSnapshot = null
      this.lastFailedSnapshot = snapshot
      this.setStatus('error')
    } finally {
      this.requestInFlight = false
      this.drainQueue()
      if (!this.requestInFlight && !this.pendingSnapshot) this.queueSettled?.()
    }
  }

  setStatus (state) {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = state === 'saved'
      ? this.element.dataset.autosaveSavedMessage
      : this.element.dataset.autosaveErrorMessage
  }

  restore (snapshot) {
    this.inputTargets.filter((input) => input.name === 'rider[preferred_travel_modes][]').forEach((input) => {
      input.checked = snapshot.preferred_travel_modes.includes(input.value)
    })
    const email = this.inputTargets.find((input) => input.name === 'rider[email_notifications]')
    const sms = this.inputTargets.find((input) => input.name === 'rider[sms_notifications]')
    if (email) email.checked = snapshot.email_notifications
    if (sms) sms.checked = snapshot.sms_notifications
  }
}
